import baseAxios, { axiosWithoutCaseMiddleware } from '@/helpers/forms/axios';
import { createAxiosWithConfig } from '@/helpers/forms/axiosWithConfig';
import addressesFactory from './addresses';
import adminFactory from './admin';
import attachmentsFactory from './attachments';
import billingFactory from './billing';
import channelsFactory from './channels';
import companyServicesFactory from './companyServices';
import customersFactory from './customers';
import dashboardFactory from './dashboard';
import externalFactory from './external';
import fulfillmentFactory from './fulfillment';
import inventoryFactory from './inventory';
import itemGroupConfigurationsFactory from './itemGroupConfigurations';
import labelsFactory from './labels';
import merchantsFactory from './merchants';
import movesFactory from './moves';
import narrativeFactory from './narrative';
import notificationsFactory from './notifications';
import orderItemsFactory from './orderItems';
import ordersFactory from './orders';
import packageItemsFactory from './packageItems';
import packagePresetsFactory from './packagePresets';
import packagesFactory from './packages';
import partnerAppDirectoryFactory from './partnerAppDirectory';
import paymentProvidersFactory from './paymentProviders';
import pickListsFactory from './pickLists';
import platformOauthConnectionsFactory from './platformOauthConnections';
import printingFactory from './printing';
import productionBatchesFactory from './productionBatches';
import productionFormulasFactory from './productionFormulas';
import reportsFactory from './reports';
import serviceProviderBillingFactory from './serviceProviderBilling';
import serviceProviderConfigurationFactory from './serviceProviderConfiguration';
import serviceProvidersFactory from './serviceProviders';
import settingsFactory from './settings';
import shipmentsFactory from './shipments';
import spreadsheetsFactory from './spreadsheets';
import stationsFactory from './stations';
import tagsFactory from './tags';
import trackingNumbersFactory from './trackingNumbers';
import universalSearchFactory from './universalSearch/search';
import usersFactory from './users';
import warehousesFactory from './warehouses';
import webhooksFactory from './webhooks';
import workflowsFactory from './workflows';

const modelId = (param: string | null = null): number => {
    if (param != null && param !== '') {
        const pathParts = location.pathname.split('/');
        const index = pathParts.indexOf(param) + 1;
        if (index > 0 && index < pathParts.length) {
            const pathPart = pathParts[index];
            if (pathPart != null && pathPart !== '') {
                const id = Number.parseInt(pathPart);
                return Number.isNaN(id) ? -1 : id;
            }
        }
    }

    const RegExp = /\d+(?=\D*$)/;
    const match = location.pathname.match(RegExp);
    if (match && match.length && match[0]) {
        return Number.parseInt(match[0]);
    }

    return -1;
};

// Reads the path segment after `param`. There is deliberately no unnamed fallback: a
// pattern match over the whole path would pick up the /m_$UUID merchant segment.
const modelUuid = (param: string): string | null => {
    const pathParts = location.pathname.split('/');
    const index = pathParts.indexOf(param) + 1;
    if (index > 0 && index < pathParts.length) {
        const id = pathParts[index];
        if (id != null && id !== '') {
            return id;
        }
    }

    return null;
};

// Define config interface
export interface APIConfig {
    notify?: boolean
    signal?: AbortSignal
    merchantId?: number
    serviceProviderId?: number
}

// Helper to create API modules with bound config
function createAPIWithConfig(config?: APIConfig): {
    modelId: (param?: string | null) => number
    modelUuid: (param: string) => string | null
    addresses: ReturnType<typeof addressesFactory>
    attachments: ReturnType<typeof attachmentsFactory>
    billing: ReturnType<typeof billingFactory>
    channels: ReturnType<typeof channelsFactory>
    fulfillment: ReturnType<typeof fulfillmentFactory>
    packagePresets: ReturnType<typeof packagePresetsFactory>
    spreadsheets: ReturnType<typeof spreadsheetsFactory>
    trackingNumbers: ReturnType<typeof trackingNumbersFactory>
    users: ReturnType<typeof usersFactory>
    orders: ReturnType<typeof ordersFactory>
    pickLists: ReturnType<typeof pickListsFactory>
    admin: ReturnType<typeof adminFactory>
    companyServices: ReturnType<typeof companyServicesFactory>
    customers: ReturnType<typeof customersFactory>
    dashboard: ReturnType<typeof dashboardFactory>
    external: ReturnType<typeof externalFactory>
    inventory: ReturnType<typeof inventoryFactory>
    itemGroupConfigurations: ReturnType<typeof itemGroupConfigurationsFactory>
    labels: ReturnType<typeof labelsFactory>
    merchants: ReturnType<typeof merchantsFactory>
    moves: ReturnType<typeof movesFactory>
    narrative: ReturnType<typeof narrativeFactory>
    notifications: ReturnType<typeof notificationsFactory>
    orderItems: ReturnType<typeof orderItemsFactory>
    packageItems: ReturnType<typeof packageItemsFactory>
    packages: ReturnType<typeof packagesFactory>
    partnerAppDirectory: ReturnType<typeof partnerAppDirectoryFactory>
    paymentProviders: ReturnType<typeof paymentProvidersFactory>
    platformOauthConnections: ReturnType<typeof platformOauthConnectionsFactory>
    printing: ReturnType<typeof printingFactory>
    productionBatches: ReturnType<typeof productionBatchesFactory>
    productionFormulas: ReturnType<typeof productionFormulasFactory>
    reports: ReturnType<typeof reportsFactory>
    serviceProviderBilling: ReturnType<typeof serviceProviderBillingFactory>
    serviceProviderConfiguration: ReturnType<typeof serviceProviderConfigurationFactory>
    serviceProviders: ReturnType<typeof serviceProvidersFactory>
    settings: ReturnType<typeof settingsFactory>
    shipments: ReturnType<typeof shipmentsFactory>
    stations: ReturnType<typeof stationsFactory>
    tags: ReturnType<typeof tagsFactory>
    universalSearch: ReturnType<typeof universalSearchFactory>
    warehouses: ReturnType<typeof warehousesFactory>
    webhooks: ReturnType<typeof webhooksFactory>
    workflows: ReturnType<typeof workflowsFactory>
    inboundShipments: ReturnType<typeof shipmentsFactory>['inbound']
    outboundShipments: ReturnType<typeof shipmentsFactory>['outbound']
} {
    // Create axios wrapper with config (has case conversion middleware)
    const axiosWithConfig = config
        ? createAxiosWithConfig(baseAxios, config)
        : baseAxios;

    // Axios instance for migrated modules (no case conversion)
    const migratedAxios = config
        ? createAxiosWithConfig(axiosWithoutCaseMiddleware, config)
        : axiosWithoutCaseMiddleware;

    const shipmentsModule = shipmentsFactory(axiosWithConfig, migratedAxios);

    return {
        // Keep utility functions (don't use axios):
        modelId,
        modelUuid,

        // Migrated modules (no case conversion):
        addresses: addressesFactory(migratedAxios),
        attachments: attachmentsFactory(migratedAxios),
        billing: billingFactory(migratedAxios),
        channels: channelsFactory(migratedAxios),
        companyServices: companyServicesFactory(migratedAxios),
        fulfillment: fulfillmentFactory(migratedAxios),
        inventory: inventoryFactory(migratedAxios),
        merchants: merchantsFactory(migratedAxios),
        packagePresets: packagePresetsFactory(migratedAxios),
        printing: printingFactory(migratedAxios),
        spreadsheets: spreadsheetsFactory(migratedAxios),
        trackingNumbers: trackingNumbersFactory(migratedAxios),
        users: usersFactory(migratedAxios),

        orders: ordersFactory(migratedAxios),
        pickLists: pickListsFactory(migratedAxios),

        // Not yet migrated:
        admin: adminFactory(axiosWithConfig),
        customers: customersFactory(axiosWithConfig),
        dashboard: dashboardFactory(axiosWithConfig),
        external: externalFactory(axiosWithConfig),
        itemGroupConfigurations: itemGroupConfigurationsFactory(axiosWithConfig),
        labels: labelsFactory(axiosWithConfig),
        moves: movesFactory(axiosWithConfig),
        narrative: narrativeFactory(axiosWithConfig),
        notifications: notificationsFactory(axiosWithConfig, migratedAxios),
        orderItems: orderItemsFactory(axiosWithConfig),
        packageItems: packageItemsFactory(axiosWithConfig),
        packages: packagesFactory(axiosWithConfig),
        partnerAppDirectory: partnerAppDirectoryFactory(axiosWithConfig),
        paymentProviders: paymentProvidersFactory(axiosWithConfig),
        platformOauthConnections: platformOauthConnectionsFactory(axiosWithConfig),
        productionBatches: productionBatchesFactory(axiosWithConfig),
        productionFormulas: productionFormulasFactory(axiosWithConfig),
        reports: reportsFactory(axiosWithConfig),
        serviceProviderBilling: serviceProviderBillingFactory(axiosWithConfig),
        serviceProviderConfiguration: serviceProviderConfigurationFactory(axiosWithConfig),
        serviceProviders: serviceProvidersFactory(axiosWithConfig),
        settings: settingsFactory(axiosWithConfig),
        shipments: shipmentsModule,
        stations: stationsFactory(axiosWithConfig),
        tags: tagsFactory(axiosWithConfig),
        universalSearch: universalSearchFactory(axiosWithConfig),
        warehouses: warehousesFactory(axiosWithConfig),
        webhooks: webhooksFactory(axiosWithConfig),
        workflows: workflowsFactory(axiosWithConfig),

        // Keep nested shipments for backward compatibility:
        inboundShipments: shipmentsModule.inbound,
        outboundShipments: shipmentsModule.outbound,
    };
}

// Create default API instance
const defaultAPI = createAPIWithConfig();

// Define factory function interface
interface APIFactory {
    (config?: APIConfig): typeof defaultAPI;
}

// Create the API as both function and object
type APIType = APIFactory & typeof defaultAPI;
const API: APIType = Object.assign(
    (config?: APIConfig) => {
        if (!config) return defaultAPI;
        return createAPIWithConfig(config);
    },
    defaultAPI,
);

export default API;
